import { isNativeApp } from './nativePlatform';

/**
 * Lightweight, native-only diagnostics buffer.
 *
 * Auth on iOS bounces the WebView out to the system browser and back, which
 * reloads the page and wipes any in-memory state. We persist entries in
 * localStorage so the on-screen overlay can show what happened across that
 * reload (incoming deep-link URL, MSAL redirect result, etc.).
 *
 * All functions are no-ops on web so this never affects the browser/SWA build.
 */

const STORAGE_KEY = 'rmd.native.diag';
const OPEN_STORAGE_KEY = 'rmd.native.diag.open';
const MAX_ENTRIES = 80;

export interface NativeDiagnosticEntry {
  time: string;
  message: string;
  detail?: string;
}

type Listener = (entries: NativeDiagnosticEntry[]) => void;

const listeners = new Set<Listener>();

const readEntries = (): NativeDiagnosticEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeEntries = (entries: NativeDiagnosticEntry[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Ignore storage failures — diagnostics must never break the app.
  }
};

const stringifyDetail = (detail: unknown): string | undefined => {
  if (detail === undefined || detail === null) {
    return undefined;
  }
  if (typeof detail === 'string') {
    return detail;
  }
  if (detail instanceof Error) {
    return `${detail.name}: ${detail.message}`;
  }
  try {
    return JSON.stringify(detail);
  } catch {
    return String(detail);
  }
};

export const recordNativeDiagnostic = (message: string, detail?: unknown): void => {
  if (!isNativeApp()) {
    return;
  }

  const entry: NativeDiagnosticEntry = {
    time: new Date().toISOString().slice(11, 23),
    message,
    detail: stringifyDetail(detail)
  };

  const entries = [...readEntries(), entry].slice(-MAX_ENTRIES);
  writeEntries(entries);
  listeners.forEach((listener) => listener(entries));
};

export const getNativeDiagnostics = (): NativeDiagnosticEntry[] => {
  if (!isNativeApp()) {
    return [];
  }
  return readEntries();
};

export const clearNativeDiagnostics = (): void => {
  if (!isNativeApp()) {
    return;
  }
  writeEntries([]);
  listeners.forEach((listener) => listener([]));
};

export const subscribeNativeDiagnostics = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const OPEN_EVENT = 'rmd:open-native-diagnostics';

const readOpenPreference = (): boolean => {
  try {
    return localStorage.getItem(OPEN_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const writeOpenPreference = (open: boolean): void => {
  try {
    if (open) {
      localStorage.setItem(OPEN_STORAGE_KEY, '1');
      return;
    }
    localStorage.removeItem(OPEN_STORAGE_KEY);
  } catch {
    // Ignore storage failures — diagnostics must never break the app.
  }
};

export const shouldOpenNativeDiagnostics = (): boolean => {
  if (!isNativeApp()) {
    return false;
  }
  return readOpenPreference();
};

export const setNativeDiagnosticsOpen = (open: boolean): void => {
  if (!isNativeApp()) {
    return;
  }
  writeOpenPreference(open);
};

/** Native-only: request the on-screen diagnostics panel to open. */
export const openNativeDiagnostics = (): void => {
  if (!isNativeApp()) {
    return;
  }
  writeOpenPreference(true);
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
};

/** Native-only: request the on-screen diagnostics panel to close. */
export const closeNativeDiagnostics = (): void => {
  if (!isNativeApp()) {
    return;
  }
  writeOpenPreference(false);
};

/** Subscribe to open requests (used by the overlay component). */
export const subscribeOpenNativeDiagnostics = (listener: () => void): (() => void) => {
  window.addEventListener(OPEN_EVENT, listener);
  return () => {
    window.removeEventListener(OPEN_EVENT, listener);
  };
};
